import React, { useState } from 'react';
import { apiClient } from '../services/apiClient';

interface ContainerTestResult {
  success: boolean;
  data?: {
    containerId: string;
    containerUrl: string;
    isNew: boolean;
    matterId: string;
  };
  message?: string;
  error?: string;
}

const ContainerTestPage: React.FC = () => {
  const [matterId, setMatterId] = useState('test-matter-' + Date.now());
  const [matterName, setMatterName] = useState('Test Matter for Container');
  const [clientId, setClientId] = useState('test-client-456');
  const [result, setResult] = useState<ContainerTestResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const testContainerCreation = async () => {
    setIsLoading(true);
    setResult(null);
    
    try {
      console.log('Testing container creation with:', { matterId, matterName, clientId });
      const response = await apiClient.ensureMatterContainer(matterId, matterName, clientId);
      setResult(response);
      console.log('Container test result:', response);
    } catch (error) {
      console.error('Container test failed:', error);
      setResult({
        success: false,
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateNewTest = () => {
    setMatterId('test-matter-' + Date.now());
    setMatterName('Test Matter for Container ' + new Date().toLocaleTimeString());
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-700 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-slate-800 rounded-lg shadow-lg border border-slate-600 p-6">
          <h1 className="text-2xl font-bold text-white mb-6">
            🧪 Container Management Test Page
          </h1>
          
          <div className="text-slate-300 mb-6">
            <p>Test the new backend container management API with application permissions.</p>
            <p className="text-sm mt-2">This bypasses database operations and directly tests SharePoint Embedded container creation.</p>
          </div>

          {/* Test Form */}
          <div className="bg-slate-700 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-white mb-4">Container Test Parameters</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Matter ID
                </label>
                <input
                  type="text"
                  value={matterId}
                  onChange={(e) => setMatterId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter matter ID"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Matter Name
                </label>
                <input
                  type="text"
                  value={matterName}
                  onChange={(e) => setMatterName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter matter name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Client ID
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded-md text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter client ID"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={testContainerCreation}
                disabled={isLoading || !matterId || !matterName || !clientId}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Testing...
                  </>
                ) : (
                  '🚀 Test Container Creation'
                )}
              </button>
              
              <button
                onClick={generateNewTest}
                className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                🔄 Generate New Test
              </button>
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="bg-slate-700 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4">Test Results</h2>
              
              {result.success ? (
                <div className="bg-green-900/50 border border-green-600 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-green-400 text-xl">✅</span>
                    <h3 className="text-green-300 font-semibold">Success!</h3>
                  </div>
                  
                  {result.data && (
                    <div className="text-slate-300 space-y-2">
                      <p><strong>Container ID:</strong> <code className="bg-slate-800 px-2 py-1 rounded text-green-300">{result.data.containerId}</code></p>
                      <p><strong>Matter ID:</strong> {result.data.matterId}</p>
                      <p><strong>Container Status:</strong> {result.data.isNew ? '🆕 Newly Created' : '♻️ Existing Container'}</p>
                      <p><strong>Message:</strong> {result.message}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-red-900/50 border border-red-600 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-red-400 text-xl">❌</span>
                    <h3 className="text-red-300 font-semibold">Failed</h3>
                  </div>
                  
                  <div className="text-slate-300">
                    <p><strong>Error:</strong> {result.error || result.message}</p>
                  </div>
                </div>
              )}
              
              <details className="mt-4">
                <summary className="text-slate-400 cursor-pointer hover:text-slate-300">Raw Response</summary>
                <pre className="mt-2 p-3 bg-slate-800 rounded text-xs text-slate-300 overflow-auto">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          )}

          {/* Info */}
          <div className="mt-6 bg-blue-900/30 border border-blue-600 rounded-lg p-4">
            <h3 className="text-blue-300 font-semibold mb-2">ℹ️ What This Tests</h3>
            <ul className="text-slate-300 text-sm space-y-1">
              <li>• Backend API authentication with Azure AD application permissions</li>
              <li>• SharePoint Embedded container creation using Microsoft Graph API</li>
              <li>• Frontend-to-backend API communication</li>
              <li>• Error handling and response parsing</li>
              <li>• Container management service functionality</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContainerTestPage;
